import Dexie, { type EntityTable, type Table } from 'dexie';
import type { Chain } from '@/domain/crm/entity/Chain';
import type { User } from '@/domain/entity/User';
import type { Property } from '@/domain/crm/entity/Property';
import type { Company } from '@/domain/crm/entity/Company';
import type { Contact } from '@/domain/crm/entity/Contact';
import type { Query } from '@/domain/crm/entity/Query';

export interface CacheMemberRow {
  cacheKey: string
  entityId: string | number
}

export type schema = {
  // Core entities that need local caching for performance
  properties: EntityTable<Property, 'id'>;
  chains: EntityTable<Chain, 'id'>;
  companies: EntityTable<Company, 'id'>;
  contacts: EntityTable<Contact, 'id'>;
  users: EntityTable<User, 'id'>;

  // Search and app state
  queries: EntityTable<Query, 'id'>;
  application: EntityTable<Record<string, any>, 'id'>;

  // Junction tables — maps (cacheKey, entityId) for multi-cache support
  propertyCacheMembers: Table<CacheMemberRow, [string, string | number]>;
  chainCacheMembers: Table<CacheMemberRow, [string, string | number]>;
  companyCacheMembers: Table<CacheMemberRow, [string, string | number]>;
  contactCacheMembers: Table<CacheMemberRow, [string, string | number]>;
};

const db = new Dexie("slingshot") as Dexie & schema;

// Version 5 — kept for Dexie upgrade path
db.version(5).stores({
  properties: "&id, name, status, type, chain.primaryCategory, chain.store.id, userId, _cacheKey, *tenantIds, *chain.categories, *contactIds",
  chains: "&id, chainId, name, logo, tracked, _cacheKey",
  companies: "&id, name, _cacheKey",
  contacts: "&id, name, email, companyId, _cacheKey",
  users: "&id, _cacheKey",
  queries: "&id, name, userId, _cacheKey",
  application: "&id, _cacheKey"
});

// Version 6 — Junction table architecture (fixes cache corruption from overlapping searches)
db.version(6).stores({
  // Entity tables — _cacheKey removed (junction tables handle membership now)
  properties: "&id, name, status, type, chain.primaryCategory, chain.store.id, userId, *tenantIds, *chain.categories, *contactIds",
  chains: "&id, chainId, name, logo, tracked",
  companies: "&id, name",
  contacts: "&id, name, email, companyId",
  users: "&id",
  queries: "&id, name, userId",
  application: "&id",

  // Junction tables — compound PK [cacheKey+entityId], indexed both ways
  propertyCacheMembers: "[cacheKey+entityId], cacheKey, entityId",
  chainCacheMembers: "[cacheKey+entityId], cacheKey, entityId",
  companyCacheMembers: "[cacheKey+entityId], cacheKey, entityId",
  contactCacheMembers: "[cacheKey+entityId], cacheKey, entityId"
}).upgrade(tx => Promise.all([
  tx.table('properties').clear(),
  tx.table('chains').clear(),
  tx.table('companies').clear(),
  tx.table('contacts').clear(),
  tx.table('application').clear()
]));

// Clear all tables
const clearAllTables = async () => {
  const tableNames = db.tables.map(table => table.name);
  for (const tableName of tableNames) {
    await db.table(tableName).clear();
  }
};

export {
  db,
  clearAllTables
};
